// ==========================================================================
// MÉMO JUSTIFICATIF — COUCHE LIVRABLE PARTAGÉE DES CAS PRATIQUES
// Extrait de PariteFusion.jsx (lot 0 du module Mali) : writer PDF maison
// (zéro dépendance), aperçu papier écran et téléchargement. Chaque module
// construit son propre modèle de blocs ; le même modèle alimente l'aperçu
// (<MemoPaper/>) ET l'export (buildMemoPdf) — le livrable est exactement
// ce qui est prévisualisé.
//
// Types de blocs : head {title, sub, op, date} · h {text} · p {text, strong?}
// · quote {text} · note {text} · table {head, rows, cols, align?, strongCol?,
// clip?}. Pour table : cols = largeurs PDF en points (obligatoire),
// align = "l"/"r" par colonne (défaut : colonnes ≥ 2 à droite),
// strongCol = index de la colonne en gras (défaut : dernière),
// clip = nb max de caractères par cellule (défaut : 26 col. 0, 16 ensuite).
// ==========================================================================
import React from "react";

// Charte PRINT du rapport de synthèse (fond blanc, encre foncée, or texte
// #8a6d1f, filets #C9A84C). ASCII Type1.
export const PRINT = { INK: "#222222", INK2: "#555555", MUTE: "#777777", GOLD: "#8a6d1f", GOLDLINE: "#C9A84C", PALE: "#f7f6f2", RULE: "#d8d5cc" };
// Encodage WinAnsi (CP1252) des polices Type1 : les accents français sont
// émis en échappements octaux (\\351 = é…) — la sortie reste ASCII pur, donc
// les /Length et offsets xref comptés en caractères restent exacts en octets.
const WIN_EXTRA = { "€": 0x80, "‚": 0x82, "„": 0x84, "…": 0x85, "‘": 0x91, "’": 0x92, "“": 0x93, "”": 0x94, "•": 0x95, "–": 0x96, "—": 0x97, "™": 0x99, "œ": 0x9c, "Œ": 0x8c, "Ÿ": 0x9f };
// Les espaces insécables (fines \u202f des montants toLocaleString comprises)
// sont préservés en \u00a0 (WinAnsi \\240) et le wrap ne coupe que sur les
// espaces ordinaires : un montant ne peut jamais être coupé en fin de ligne —
// exigence de registre des livrables (documents opposables).
const pdfNorm = (s) => String(s ?? "")
  .replace(/\u202f/g, "\u00a0").replace(/[≤⩽]/g, "<=").replace(/≥/g, ">=").replace(/⇒/g, "=>").replace(/−/g, "-")
  .replace(/[✓✗ⓘ]/g, "").replace(/⚖️?/gu, "");
const escPdf = (s) => {
  let out = "";
  for (const ch of pdfNorm(s)) {
    const c = ch.codePointAt(0);
    if (ch === "\\") out += "\\\\";
    else if (ch === "(") out += "\\(";
    else if (ch === ")") out += "\\)";
    else if (c >= 32 && c <= 126) out += ch;
    else if (WIN_EXTRA[ch] != null) out += "\\" + WIN_EXTRA[ch].toString(8).padStart(3, "0");
    else if (c >= 0xa0 && c <= 0xff) out += "\\" + c.toString(8).padStart(3, "0");
    else out += "?";
  }
  return out;
};

export const buildMemoPdf = (blocks, footerText) => {
  const W = 595.28, H = 841.89, mL = 48, mR = 48, mT = 56, mB = 62, cw = W - mL - mR;
  const rgb = (h) => `${(parseInt(h.slice(1, 3), 16) / 255).toFixed(3)} ${(parseInt(h.slice(3, 5), 16) / 255).toFixed(3)} ${(parseInt(h.slice(5, 7), 16) / 255).toFixed(3)}`;
  const esc = escPdf;
  const pages = []; let ops = []; let y = H - mT;
  const flush = () => { pages.push(ops); ops = []; y = H - mT; };
  const ensure = (h) => { if (y - h < mB) flush(); };
  const put = (s, x, size, font, color) => ops.push(`BT /${font} ${size} Tf ${rgb(color)} rg ${x.toFixed(2)} ${y.toFixed(2)} Td (${esc(s)}) Tj ET`);
  const wrap = (s, size, width) => {
    const max = Math.max(8, Math.floor(width / (size * 0.5)));
    const words = pdfNorm(s).split(/ +/); const lines = []; let cur = "";
    for (const w of words) { if ((cur + " " + w).trim().length > max) { if (cur) lines.push(cur); cur = w; } else cur = (cur + " " + w).trim(); }
    if (cur) lines.push(cur);
    return lines.length ? lines : [""];
  };
  const para = (s, { size = 9.5, font = "F1", color = PRINT.INK, x = mL, width = cw, lh } = {}) => {
    const step = lh || size * 1.5;
    for (const line of wrap(s, size, width)) { ensure(step); y -= step; put(line, x, size, font, color); }
  };
  const gap = (h) => { y -= h; };
  const hLine = (color, w2, x1 = mL, x2 = W - mR) => ops.push(`${rgb(color)} RG ${w2} w ${x1.toFixed(2)} ${y.toFixed(2)} m ${x2.toFixed(2)} ${y.toFixed(2)} l S`);
  const rectFill = (x, w2, h, color) => ops.push(`${rgb(color)} rg ${x.toFixed(2)} ${(y - h).toFixed(2)} ${w2.toFixed(2)} ${h.toFixed(2)} re f`);
  for (const bl of blocks) {
    if (bl.type === "head") {
      gap(6); y -= 11; put(bl.title, mL, 11, "F2", PRINT.GOLD);
      y -= 20; put(bl.sub, mL, 16, "F2", PRINT.INK);
      y -= 15; put(bl.op, mL, 10, "F1", PRINT.INK2);
      y -= 13; put(bl.date, mL, 9, "F1", PRINT.MUTE);
      gap(10); hLine(PRINT.GOLDLINE, 1.2); gap(4);
    } else if (bl.type === "h") {
      // Anti-orphelin : un titre ne reste jamais seul en bas de page — on
      // réserve sa hauteur (34) plus ~2 lignes de paragraphe (29) avant de
      // le poser, sinon il bascule en page suivante avec son contenu.
      ensure(63); gap(16); y -= 11; put(bl.text, mL, 11, "F2", PRINT.GOLD); gap(5); hLine(PRINT.RULE, 0.6); gap(2);
    } else if (bl.type === "p") {
      gap(3); para(bl.text, { font: bl.strong ? "F2" : "F1" });
    } else if (bl.type === "quote") {
      gap(4);
      const lines = wrap(bl.text, 9.5, cw - 18);
      for (const line of lines) { ensure(14.5); y -= 14.5; ops.push(`${rgb(PRINT.GOLDLINE)} RG 1.4 w ${(mL + 2).toFixed(2)} ${(y - 3).toFixed(2)} m ${(mL + 2).toFixed(2)} ${(y + 9).toFixed(2)} l S`); put(line, mL + 14, 9.5, "F3", PRINT.INK2); }
    } else if (bl.type === "note") {
      gap(5); para(bl.text, { size: 8, font: "F3", color: PRINT.MUTE, lh: 11.5 });
    } else if (bl.type === "table") {
      const COLS = bl.cols;
      const alignOf = (i) => (bl.align ? bl.align[i] : i >= 2 ? "r" : "l");
      const strongCol = bl.strongCol != null ? bl.strongCol : bl.head.length - 1;
      const rowH = 16;
      ensure(rowH * 2 + 8); gap(6);
      rectFill(mL, cw, rowH, PRINT.PALE); y -= 11.5;
      let x = mL;
      bl.head.forEach((hd, i) => { put(String(hd).slice(0, i === 0 ? 24 : 14), x + 3, 7.5, "F2", PRINT.INK2); x += COLS[i]; });
      y -= rowH - 11.5; hLine(PRINT.RULE, 0.6);
      for (const row of bl.rows) {
        ensure(rowH); y -= 11.5;
        x = mL;
        row.forEach((cell, i) => {
          const s = pdfNorm(String(cell)); const size = 8.5;
          const isNum = alignOf(i) === "r";
          const tx = isNum ? x + COLS[i] - 3 - s.length * size * 0.5 : x + 3;
          const lim = bl.clip && bl.clip[i] != null ? bl.clip[i] : i === 0 ? 26 : 16;
          put(s.slice(0, lim), Math.max(x + 3, tx), size, i === strongCol ? "F2" : "F1", PRINT.INK);
          x += COLS[i];
        });
        y -= rowH - 11.5; hLine(PRINT.RULE, 0.4);
      }
    }
  }
  flush();
  pages.forEach((p, i) => {
    p.push(`BT /F1 7.5 Tf ${rgb(PRINT.MUTE)} rg ${mL.toFixed(2)} 34 Td (${esc(footerText)}) Tj ET`);
    const pn = `${i + 1} / ${pages.length}`;
    p.push(`BT /F1 7.5 Tf ${rgb(PRINT.MUTE)} rg ${(W - mR - pn.length * 7.5 * 0.5).toFixed(2)} 34 Td (${esc(pn)}) Tj ET`);
  });
  const objs = []; const kids = [];
  objs[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objs[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
  objs[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
  objs[5] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>";
  let next = 6;
  for (const p of pages) {
    const pageNum = next++, contNum = next++;
    kids.push(`${pageNum} 0 R`);
    objs[pageNum] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R >> >> /Contents ${contNum} 0 R >>`;
    objs[contNum] = { stream: p.join("\n") };
  }
  objs[2] = `<< /Type /Pages /Kids [${kids.join(" ")}] /Count ${pages.length} >>`;
  let out = "%PDF-1.4\n"; const offs = [];
  for (let i = 1; i < next; i++) {
    offs[i] = out.length;
    const o = objs[i];
    out += typeof o === "string"
      ? `${i} 0 obj\n${o}\nendobj\n`
      : `${i} 0 obj\n<< /Length ${o.stream.length} >>\nstream\n${o.stream}\nendstream\nendobj\n`;
  }
  const xref = out.length;
  out += `xref\n0 ${next}\n0000000000 65535 f \n`;
  for (let i = 1; i < next; i++) out += String(offs[i]).padStart(10, "0") + " 00000 n \n";
  out += `trailer\n<< /Size ${next} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([out], { type: "application/pdf" });
};

// Téléchargement d'un Blob PDF côté navigateur (rien ne transite ni ne reste
// sur un serveur — cohérent avec la politique session-only).
export const downloadPdf = (blob, filename) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 800);
};

// ── Aperçu écran du mémo : rend le MÊME modèle de blocs, en style papier ──
export const MemoPaper = ({ blocks }) => (
  <div style={{ background: "#ffffff", color: PRINT.INK, borderRadius: 12, padding: "34px 40px", maxWidth: 780, border: "1px solid rgba(255,255,255,0.15)" }}>
    {blocks.map((bl, i) => {
      if (bl.type === "head") return (
        <div key={i} style={{ borderBottom: `2px solid ${PRINT.GOLDLINE}`, paddingBottom: 14, marginBottom: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: PRINT.GOLD, letterSpacing: "0.12em" }}>{bl.title}</div>
          <div style={{ fontSize: 19, fontWeight: 800, margin: "6px 0 4px" }}>{bl.sub}</div>
          <div style={{ fontSize: 12, color: PRINT.INK2 }}>{bl.op}</div>
          <div style={{ fontSize: 10.5, color: PRINT.MUTE, marginTop: 3 }}>{bl.date}</div>
        </div>
      );
      if (bl.type === "h") return <div key={i} style={{ fontSize: 12, fontWeight: 700, color: PRINT.GOLD, margin: "18px 0 6px", paddingBottom: 3, borderBottom: `1px solid ${PRINT.RULE}` }}>{bl.text}</div>;
      if (bl.type === "p") return <p key={i} style={{ fontSize: 12, lineHeight: 1.65, margin: "5px 0", fontWeight: bl.strong ? 700 : 400 }}>{bl.text}</p>;
      if (bl.type === "quote") return <p key={i} style={{ fontSize: 12, lineHeight: 1.65, margin: "6px 0", padding: "2px 0 2px 12px", borderLeft: `3px solid ${PRINT.GOLDLINE}`, fontStyle: "italic", color: PRINT.INK2 }}>{bl.text}</p>;
      if (bl.type === "note") return <p key={i} style={{ fontSize: 10, lineHeight: 1.5, margin: "8px 0 2px", fontStyle: "italic", color: PRINT.MUTE }}>{bl.text}</p>;
      if (bl.type === "table") {
        const alignOf = (j) => (bl.align ? bl.align[j] : j >= 2 ? "r" : "l");
        const strongCol = bl.strongCol != null ? bl.strongCol : bl.head.length - 1;
        return (
          <table key={i} style={{ width: "100%", borderCollapse: "collapse", margin: "8px 0", fontSize: 10.5 }}>
            <thead><tr style={{ background: PRINT.PALE }}>{bl.head.map((h, j) => <th key={j} style={{ padding: "5px 6px", textAlign: alignOf(j) === "r" ? "right" : "left", color: PRINT.INK2, fontWeight: 700, borderBottom: `1px solid ${PRINT.RULE}`, fontSize: 9.5 }}>{h}</th>)}</tr></thead>
            <tbody>{bl.rows.map((row, k) => (
              <tr key={k}>{row.map((c, j) => <td key={j} style={{ padding: "5px 6px", textAlign: alignOf(j) === "r" ? "right" : "left", borderBottom: `1px solid ${PRINT.RULE}`, fontFamily: alignOf(j) === "r" ? "monospace" : "inherit", fontWeight: j === strongCol ? 700 : 400 }}>{c}</td>)}</tr>
            ))}</tbody>
          </table>
        );
      }
      return null;
    })}
  </div>
);
